import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ImageViewerModalProps {
  open: boolean;
  images: string[];
  onClose: () => void;
  initialIndex?: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.25;

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
  open,
  images,
  onClose,
  initialIndex = 0,
}) => {
  const [index, setIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);

  const resetTransform = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (open) {
      setIndex(Math.min(initialIndex, Math.max(0, images.length - 1)));
      resetTransform();
    }
  }, [open, initialIndex, images.length, resetTransform]);

  const zoomBy = useCallback((delta: number) => {
    setScale((s) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s + delta));
      if (next <= MIN_SCALE) {
        setOffset({ x: 0, y: 0 });
      }
      return next;
    });
  }, []);

  const goTo = useCallback(
    (next: number) => {
      if (next < 0 || next >= images.length) return;
      setIndex(next);
      resetTransform();
    },
    [images.length, resetTransform]
  );

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
      setScale((s) => {
        const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s + delta));
        if (next <= MIN_SCALE) {
          setOffset({ x: 0, y: 0 });
        }
        return next;
      });
    },
    []
  );

  useEffect(() => {
    const el = viewportRef.current;
    if (!open || !el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [open, handleWheel]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft') {
        goTo(index - 1);
        return;
      }
      if (e.key === 'ArrowRight') {
        goTo(index + 1);
        return;
      }
      if (e.key === '+' || e.key === '=') {
        zoomBy(ZOOM_STEP);
        return;
      }
      if (e.key === '-') {
        zoomBy(-ZOOM_STEP);
        return;
      }
      if (e.key === '0') {
        resetTransform();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index, goTo, zoomBy, resetTransform, onClose]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (scale <= MIN_SCALE) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      ox: offset.x,
      oy: offset.y,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || scale <= MIN_SCALE) return;
    setOffset({
      x: dragStart.current.ox + (e.clientX - dragStart.current.x),
      y: dragStart.current.oy + (e.clientY - dragStart.current.y),
    });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (dragging) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      setDragging(false);
    }
  };

  const onDoubleClick = () => {
    if (scale > MIN_SCALE) {
      resetTransform();
    } else {
      setScale(2);
    }
  };

  if (!open || images.length === 0) return null;

  const currentSrc = images[index];

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/85"
      role="dialog"
      aria-modal="true"
      aria-label="查看图片"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3 text-white">
        <div className="min-w-0">
          <h3 className="text-base font-medium truncate">查看图片</h3>
          {images.length > 1 && (
            <p className="text-xs text-gray-300 mt-0.5">
              {index + 1} / {images.length}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <button
            type="button"
            onClick={() => zoomBy(-ZOOM_STEP)}
            disabled={scale <= MIN_SCALE}
            className="rounded-md px-2.5 py-1.5 text-sm bg-white/10 hover:bg-white/20 disabled:opacity-40"
            title="缩小"
          >
            −
          </button>
          <span className="text-xs tabular-nums w-12 text-center">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            onClick={() => zoomBy(ZOOM_STEP)}
            disabled={scale >= MAX_SCALE}
            className="rounded-md px-2.5 py-1.5 text-sm bg-white/10 hover:bg-white/20 disabled:opacity-40"
            title="放大"
          >
            +
          </button>
          <button
            type="button"
            onClick={resetTransform}
            className="rounded-md px-2.5 py-1.5 text-sm bg-white/10 hover:bg-white/20 hidden sm:inline"
            title="重置"
          >
            重置
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2.5 py-1.5 text-lg leading-none bg-white/10 hover:bg-white/20 ml-1"
            aria-label="关闭"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="relative flex-1 min-h-0">
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full w-10 h-10 flex items-center justify-center bg-black/50 text-white hover:bg-black/70 disabled:opacity-30"
              aria-label="上一张"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              disabled={index === images.length - 1}
              className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full w-10 h-10 flex items-center justify-center bg-black/50 text-white hover:bg-black/70 disabled:opacity-30"
              aria-label="下一张"
            >
              ›
            </button>
          </>
        )}

        <div
          ref={viewportRef}
          className={`absolute inset-0 flex items-center justify-center overflow-hidden touch-none select-none ${
            scale > MIN_SCALE ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'
          }`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
        >
          <img
            src={currentSrc}
            alt={`图片 ${index + 1}`}
            draggable={false}
            className="max-h-full max-w-full object-contain will-change-transform"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transition: dragging ? 'none' : 'transform 0.05s ease-out',
            }}
          />
        </div>
      </div>

      <p className="shrink-0 px-4 py-2 text-center text-xs text-gray-400">
        滚轮或 +/- 缩放 · 放大后可拖拽移动 · 双击切换放大/还原 · Esc 关闭
      </p>
    </div>
  );
};

export default ImageViewerModal;
